import {
  ReactElement,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
  JSX,
} from 'react'

import {
  ColumnDef,
  ColumnFiltersState,
  OnChangeFn,
  Row,
  SortingState,
  Table as TableType,
  TableState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
  getSortedRowModel,
  Column,
} from '@tanstack/react-table'
import { attempt, isError, isString, times, uniq } from 'lodash'
import { ChevronUpIcon, InfoIcon, ListFilterIcon } from 'lucide-react'

import { PageNavigator } from '~/components/PageNavigator'
import {
  Button,
  Checkbox,
  EmptyState,
  SelectInput,
  Skeleton,
  Tooltip,
} from '~/components/component-library'
import { Input } from '~/components/inputs/Input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/components/ui/popover'
import {
  Table as TableRoot,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { cn } from '~/utils/tailwind-utils'

const DEFAULT_PAGE_SIZE = 10
const PAGE_SIZES = [10, 25, 50] as const
const DEFAULT_TANSTACK_MAX_COLUMN_SIZE = Number.MAX_SAFE_INTEGER

function hasExplicitMaxSize<T extends { maxSize?: number }>(
  columnDef: T,
): columnDef is T & { maxSize: number } {
  return (
    typeof columnDef.maxSize === 'number' &&
    columnDef.maxSize < DEFAULT_TANSTACK_MAX_COLUMN_SIZE
  )
}

// Columns that declare both minSize and maxSize (with min < max) flex between
// those bounds; others stay locked at getSize() (legacy behavior).
function isFlexibleColumn(columnDef: {
  minSize?: number
  maxSize?: number
}): boolean {
  return (
    typeof columnDef.minSize === 'number' &&
    hasExplicitMaxSize(columnDef) &&
    columnDef.minSize < columnDef.maxSize
  )
}

export type TablePageSize = (typeof PAGE_SIZES)[number]

type ColumnFilterVariant<TData> = {
  render: ColumnFilterVariantFunction<TData>
  isEmpty: (value: unknown) => boolean
}

type ColumnFilterVariantFunction<TData> = (
  column: Column<TData>,
  value: string,
  setFilterValue: (value: string) => void,
) => JSX.Element

interface TableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  tableRef?: RefObject<TableType<TData> | null>
  rowPath?: (row: Row<TData>) => string | undefined
  onRowClick?: (row: Row<TData>) => void
  setGlobalFilter?: (value: string) => void
  state?: Partial<TableState>
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>
  onSortingChange?: OnChangeFn<SortingState>
  onPaginationChange?: OnChangeFn<TableState['pagination']>
  emptyMessage?: string
  allowOverflow?: boolean
  rowClassName?: (row: Row<TData>) => string
  tableClassName?: string
  tableWrapperClassName?: string
  className?: string
  isLoading?: boolean
  showColumnFilters?: boolean
  showSorting?: boolean
  enableSortingRemoval?: boolean
  manualFiltering?: boolean
  pagination?:
    | {
        enabled: true
        serverSide: true
        totalCount: number
        includeFirstAndLastPage?: boolean
        initialPageSize?: number
      }
    | {
        enabled: true
        serverSide?: false
        includeFirstAndLastPage?: boolean
        initialPageSize?: number
      }
    | {
        enabled: false
        includeFirstAndLastPage?: never
      }
  columnFilterVariants?: Record<string, ColumnFilterVariant<TData>>
  skeletonCellClassName?: string
  skeletonRowCount?: number
  paginationPosition?: 'fixed' | 'absolute'
  size?: TableSize
}

export type TableSize = 'base' | 'small'

export function Table<TData, TValue>({
  columns,
  data,
  tableRef,
  rowPath,
  onRowClick,
  setGlobalFilter,
  state,
  onColumnFiltersChange,
  onSortingChange,
  onPaginationChange,
  emptyMessage,
  pagination = {
    enabled: true,
    includeFirstAndLastPage: false,
    initialPageSize: DEFAULT_PAGE_SIZE,
  },
  allowOverflow = true,
  isLoading = false,
  showColumnFilters = true,
  showSorting = false,
  enableSortingRemoval = true,
  manualFiltering = false,
  rowClassName,
  tableClassName,
  tableWrapperClassName,
  className,
  columnFilterVariants = {},
  paginationPosition = 'fixed',
  skeletonCellClassName,
  skeletonRowCount = 5,
  size = 'base',
}: TableProps<TData, TValue>) {
  const cellSizeClass = size === 'small' ? 'text-sm' : 'text-base'
  const headSizeClass = size === 'small' ? 'text-xs' : 'text-sm'
  const cellMinHeightClass = size === 'small' ? 'min-h-6' : 'min-h-8'
  // TODO: Find another library, or wait for tanstack to fix this issue
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    initialState: pagination.enabled
      ? {
          pagination: {
            pageSize: pagination.initialPageSize ?? DEFAULT_PAGE_SIZE,
            pageIndex: 0,
          },
        }
      : {},
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    ...(onColumnFiltersChange ? { onColumnFiltersChange } : {}),
    ...(onSortingChange ? { onSortingChange } : {}),
    ...(onPaginationChange ? { onPaginationChange } : {}),
    getFilteredRowModel:
      manualFiltering || (pagination.enabled && pagination.serverSide)
        ? undefined
        : getFilteredRowModel(),
    getPaginationRowModel:
      pagination.enabled && !pagination.serverSide
        ? getPaginationRowModel()
        : undefined,
    manualPagination: pagination.enabled && pagination.serverSide,
    rowCount:
      pagination.enabled && pagination.serverSide
        ? pagination.totalCount
        : undefined,
    getSortedRowModel: getSortedRowModel(),
    manualSorting: !!onSortingChange,
    enableSortingRemoval,
    state,
    enableHiding: true,
    autoResetPageIndex: false,
  })

  // Assign table to ref synchronously. useReactTable returns a new object on every render,
  // but the table instance itself is stable internally. Assigning to ref is safe since
  // ref mutations don't trigger re-renders.
  if (tableRef) {
    tableRef.current = table
  }

  const paginationPageSize = table.getState().pagination.pageSize
  const paginationNumRows = pagination.enabled
    ? pagination.serverSide
      ? pagination.totalCount
      : table.getFilteredRowModel().rows.length
    : 0
  // Smallest page size, not the current one, so a one-page fit keeps the bar.
  const showPagination = pagination.enabled && paginationNumRows > PAGE_SIZES[0]

  // min-h-full + pb-20 reserves space for the fixed-position pagination bar that
  // overlays the viewport. Absolute pagination only needs pb-20 — the pill is
  // positioned against the table wrapper, so without bottom padding it overlays
  // the last row. Inline tables (no pagination shown) skip both.
  const needsFixedPaginationSpacing =
    pagination.enabled && paginationPosition === 'fixed'
  const needsAbsolutePaginationSpacing =
    pagination.enabled && paginationPosition === 'absolute' && showPagination

  const hasRows = !!table.getRowModel().rows?.length
  const showHeader = isLoading || hasRows

  const rootRef = useRef<HTMLDivElement>(null)
  // Reset the scroll container to the top on paging (else you land mid-list).
  const scrollToTop = useCallback(() => {
    rootRef.current?.closest('[data-table-scroll]')?.scrollTo({ top: 0 })
  }, [])

  return (
    <div
      ref={rootRef}
      className={cn(
        'flex w-full flex-col space-y-4',
        { 'min-h-full pb-20': needsFixedPaginationSpacing },
        { 'pb-20': needsAbsolutePaginationSpacing },
        className,
      )}
    >
      <TableRoot
        allowOverflow={allowOverflow}
        className={cn(tableClassName)}
        wrapperClassName={tableWrapperClassName}
      >
        {showHeader && (
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} highlightOnHover={false}>
                {headerGroup.headers.map((header) => {
                  const canSort = showSorting && header.column.getCanSort()
                  const headerTooltip =
                    header.column.columnDef.meta?.headerTooltip
                  const isRightAligned =
                    header.column.columnDef.meta?.align === 'right'

                  const sortDirection = header.column.getIsSorted()
                  const sortIcon = canSort ? (
                    <div
                      className={cn(
                        '-mx-0.5 flex size-4 shrink-0 items-center justify-center rounded',
                        sortDirection
                          ? 'bg-gray-200 dark:bg-gray-700'
                          : 'opacity-0 group-hover/header:bg-gray-200 group-hover/header:opacity-30 dark:group-hover/header:bg-gray-700',
                      )}
                    >
                      <ChevronUpIcon
                        className={cn(
                          'size-3.5 text-primary',
                          sortDirection === 'desc' && 'rotate-180',
                        )}
                      />
                    </div>
                  ) : null

                  const headerLabel = (
                    <span className="inline-flex items-center gap-1">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {/* Affordance that the header has a tooltip */}
                      {headerTooltip && (
                        <InfoIcon className="size-2.5 shrink-0 text-secondary" />
                      )}
                    </span>
                  )

                  // Anchor the tooltip to the label + icon, not the full-width
                  // cell — otherwise it floats over empty space in wide columns.
                  const labelWithTooltip = headerTooltip ? (
                    <Tooltip
                      content={headerTooltip}
                      align={isRightAligned ? 'end' : 'start'}
                    >
                      {headerLabel}
                    </Tooltip>
                  ) : (
                    headerLabel
                  )

                  const headerContent = (
                    <div
                      className={cn('flex w-full items-center gap-2', {
                        'justify-end': isRightAligned,
                      })}
                    >
                      {isRightAligned && sortIcon}
                      {labelWithTooltip}
                      {!isRightAligned && sortIcon}
                      {showColumnFilters && header.column.getCanFilter() ? (
                        <Filter
                          column={header.column}
                          columnFilterVariants={columnFilterVariants}
                        />
                      ) : null}
                    </div>
                  )

                  return (
                    <TableHead
                      key={header.id}
                      size={size}
                      className={cn(headSizeClass, canSort && 'group/header')}
                      style={{
                        ...(isFlexibleColumn(header.column.columnDef)
                          ? {
                              minWidth: header.column.columnDef.minSize,
                              maxWidth: header.column.columnDef.maxSize,
                            }
                          : {
                              minWidth: header.column.getSize(),
                              ...(hasExplicitMaxSize(
                                header.column.columnDef,
                              ) && {
                                width: header.column.getSize(),
                                maxWidth: header.column.getSize(),
                              }),
                            }),
                        cursor: canSort ? 'pointer' : 'default',
                      }}
                      onClick={
                        canSort
                          ? (event) => {
                              // Ignore clicks bubbling through the React tree from
                              // portaled descendants (e.g. the filter popover).
                              if (
                                !event.currentTarget.contains(
                                  event.target as Node,
                                )
                              )
                                return
                              header.column.getToggleSortingHandler()?.(event)
                            }
                          : undefined
                      }
                    >
                      {header.isPlaceholder ? null : headerContent}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
        )}
        <TableBody>
          {isLoading ? (
            times(skeletonRowCount).map((rowIndex) => {
              return (
                <TableRow
                  key={`skeleton-row-${rowIndex}`}
                  highlightOnHover={false}
                >
                  {times(table.getVisibleFlatColumns().length).map(
                    (colIndex) => {
                      return (
                        <TableCell
                          key={`skeleton-cell-${colIndex}`}
                          size={size}
                        >
                          <Skeleton
                            className={cn('h-6 w-full', skeletonCellClassName)}
                          />
                        </TableCell>
                      )
                    },
                  )}
                </TableRow>
              )
            })
          ) : hasRows ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                className={cn(
                  rowClassName ? rowClassName(row) : '',
                  onRowClick || rowPath ? 'cursor-pointer' : undefined,
                )}
                onClick={() => onRowClick?.(row)}
              >
                {row.getVisibleCells().map((cell) => {
                  const align = cell.column.columnDef.meta?.align ?? 'left'
                  const isActionColumn =
                    cell.column.columnDef.meta?.isActionColumn === true
                  return (
                    <TableCell
                      key={cell.id}
                      size={size}
                      style={
                        isFlexibleColumn(cell.column.columnDef)
                          ? {
                              minWidth: cell.column.columnDef.minSize,
                              maxWidth: cell.column.columnDef.maxSize,
                            }
                          : {
                              minWidth: cell.column.getSize(),
                              ...(hasExplicitMaxSize(cell.column.columnDef) && {
                                width: cell.column.getSize(),
                                maxWidth: cell.column.getSize(),
                              }),
                            }
                      }
                      className={cn(
                        cellSizeClass,
                        cell.column.columnDef.meta &&
                          'cellClassName' in cell.column.columnDef.meta &&
                          isString(cell.column.columnDef.meta.cellClassName)
                          ? cell.column.columnDef.meta.cellClassName
                          : undefined,
                      )}
                      path={isActionColumn ? undefined : rowPath?.(row)}
                    >
                      <div
                        className={cn('flex items-center', cellMinHeightClass, {
                          'justify-start': align === 'left',
                          'justify-end': align === 'right',
                        })}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </div>
                    </TableCell>
                  )
                })}
              </TableRow>
            ))
          ) : (
            <TableRow highlightOnHover={false}>
              <TableCell
                colSpan={columns.length}
                className="h-24 px-0 py-3 text-center"
              >
                <EmptyState title="No entries found">{emptyMessage}</EmptyState>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </TableRoot>
      {showPagination && (
        <div
          className={cn(
            paginationPosition === 'absolute'
              ? 'absolute bottom-4 right-4 z-10'
              : 'fixed bottom-4 right-4 z-10',
          )}
        >
          <div className="rounded-xl border border-gray-200 bg-white/60 p-1.5 backdrop-blur-md dark:border-gray-700 dark:bg-black/60">
            <Pagination
              table={table}
              pageSize={paginationPageSize}
              numRows={paginationNumRows}
              includeFirstAndLastPage={!!pagination.includeFirstAndLastPage}
              scrollToTop={scrollToTop}
            />
          </div>
        </div>
      )}
    </div>
  )
}

type MultiselectFilter = {
  variant: 'multiselect'
  options: { value: string; display: ReactElement }[]
}

type StringFilter = {
  variant: 'string'
}

type CustomFilter = {
  variant: 'custom'
  customVariant: string
  [key: string]: unknown
}

export type TableFilterMeta = {
  filter: (MultiselectFilter | StringFilter | CustomFilter) & {
    label: string
    defaultValue?: string // if set, icon only shows active when value differs from default
  }
  align?: 'left' | 'right'
  cellClassName?: string
}

const Filter = <T,>({
  column,
  columnFilterVariants,
}: {
  column: Column<T>
  columnFilterVariants: Record<string, ColumnFilterVariant<T>>
}) => {
  const columnFilterValue = column.getFilterValue()?.toString() ?? ''
  const meta = column.columnDef.meta as TableFilterMeta

  const [isOpen, setIsOpen] = useState(false)
  const [filterValue, setFilterValue] = useState(columnFilterValue)

  useEffect(() => {
    setFilterValue(columnFilterValue)
  }, [columnFilterValue])

  const hasChanged = filterValue !== columnFilterValue

  const applyFilter = () => {
    column.setFilterValue(filterValue)
    setIsOpen(false)
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    applyFilter()
  }

  if (!meta || !meta.filter) {
    // eslint-disable-next-line no-console
    console.warn(
      `Invalid filter meta for column ${column.id}. Please add a filter property to the column meta.`,
    )
    return null
  }

  const { variant, label, defaultValue } = meta.filter

  const getIsFilterValueEmpty = (value: string) => {
    return variant === 'string'
      ? value.trim() === ''
      : variant === 'multiselect'
        ? value.length === 0 || isMultiSelectFilterValueEmpty(value)
        : columnFilterVariants[meta.filter.customVariant]
          ? columnFilterVariants[meta.filter.customVariant].isEmpty(value)
          : false
  }

  const isLocalValueEmpty = getIsFilterValueEmpty(filterValue)
  const currentFilterValue = column.getFilterValue()?.toString() ?? ''
  // Icon is "active" (blue) when filter differs from default (if set) or is non-empty (if no default)
  const isFilterActive = defaultValue
    ? currentFilterValue !== defaultValue
    : !getIsFilterValueEmpty(currentFilterValue)

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative"
          onClick={(event) => event.stopPropagation()}
        >
          <ListFilterIcon
            className={cn('h-4 w-4', { 'text-blue-500': isFilterActive })}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="mt-2">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {variant === 'string' ? (
            <FilterLabel label={label}>
              <Input
                value={filterValue}
                onChange={(event) => {
                  setFilterValue(event.target.value)
                }}
                className="px-2 py-1 text-xs"
              />
            </FilterLabel>
          ) : variant === 'multiselect' ? (
            <FilterLabel label={label}>
              <MultiSelectFilterInput
                filter={meta.filter}
                filterValue={filterValue}
                setFilterValue={setFilterValue}
              />
            </FilterLabel>
          ) : variant === 'custom' ? (
            columnFilterVariants[meta.filter.customVariant] ? (
              columnFilterVariants[meta.filter.customVariant].render(
                column,
                filterValue,
                setFilterValue,
              )
            ) : null
          ) : null}
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              size="small"
              onClick={() => {
                const resetValue = defaultValue ?? ''
                column.setFilterValue(resetValue)
                setFilterValue(resetValue)
              }}
              isDisabled={
                defaultValue ? filterValue === defaultValue : isLocalValueEmpty
              }
            >
              Reset
            </Button>
            <Button
              size="small"
              type="button"
              onClick={applyFilter}
              isDisabled={!hasChanged}
            >
              Apply
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  )
}

const FilterLabel = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium text-secondary">{label}</label>
      {children}
    </div>
  )
}

export const parseMultiSelectFilterValue = (
  value: unknown,
): string[] | Error => {
  const attemptedPrevValue = attempt(
    () => JSON.parse(value as string) as string[],
  )

  return attemptedPrevValue
}

const isMultiSelectFilterValueEmpty = (value: unknown): boolean => {
  const parsed = parseMultiSelectFilterValue(value)

  if (isError(parsed)) {
    return true
  }

  return parsed.length === 0
}

const MultiSelectFilterInput = ({
  filter,
  filterValue,
  setFilterValue,
}: {
  filter: MultiselectFilter
  filterValue: string
  setFilterValue: (value: string) => void
}) => {
  const selectedOptions = filterValue
    ? (() => {
        const parsed = parseMultiSelectFilterValue(filterValue)

        if (isError(parsed)) {
          return []
        }

        return parsed
      })()
    : []

  return (
    <div className="flex max-h-48 flex-col gap-2 overflow-y-auto">
      {filter.options.map((option) => (
        <div
          key={option.value}
          className="flex items-center gap-2"
          onClick={() => {
            setFilterValue(
              JSON.stringify(
                selectedOptions.includes(option.value)
                  ? selectedOptions.filter((value) => value !== option.value)
                  : uniq([...selectedOptions, option.value]),
              ),
            )
          }}
        >
          <Checkbox
            id={option.value}
            isChecked={selectedOptions.includes(option.value)}
          />
          <label htmlFor={option.value}>{option.display}</label>
        </div>
      ))}
    </div>
  )
}

interface IPaginationProps<T> {
  table: TableType<T>
  pageSize: number
  numRows: number
  includeFirstAndLastPage?: boolean
  scrollToTop: () => void
}

const Pagination = <T,>({
  table,
  pageSize,
  numRows,
  includeFirstAndLastPage = false,
  scrollToTop,
}: IPaginationProps<T>): JSX.Element => {
  const pageIndex = table.getState().pagination.pageIndex

  const pageCountStart = numRows === 0 ? 0 : pageIndex * pageSize + 1
  const pageCountEnd = Math.min((pageIndex + 1) * pageSize, numRows)
  const pageCountTotal = numRows

  return (
    <PageNavigator
      mode="numbered"
      pageCountStart={pageCountStart}
      pageCountEnd={pageCountEnd}
      pageCountTotal={pageCountTotal}
      canGoPreviousPage={table.getCanPreviousPage()}
      canGoNextPage={table.getCanNextPage()}
      goToFirstPage={() => {
        table.firstPage()
        scrollToTop()
      }}
      goToPreviousPage={() => {
        table.previousPage()
        scrollToTop()
      }}
      goToNextPage={() => {
        table.nextPage()
        scrollToTop()
      }}
      goToLastPage={() => {
        table.lastPage()
        scrollToTop()
      }}
      includeFirstAndLastPage={includeFirstAndLastPage}
      trailing={<PageSizeSelector table={table} scrollToTop={scrollToTop} />}
    />
  )
}

const PageSizeSelector = <T,>({
  table,
  scrollToTop,
}: {
  table: TableType<T>
  scrollToTop: () => void
}) => {
  // This is a workaround to avoid the page size selector from flickering when the page size changes
  const [localPageSize, setLocalPageSize] = useState(
    table.getState().pagination.pageSize,
  )

  return (
    <SelectInput
      selectedOption={localPageSize}
      options={PAGE_SIZES.map((pageSize) => ({
        value: pageSize,
        label: pageSize.toString(),
      }))}
      placeholder="Select page size"
      selectValue={(value: number) => {
        setLocalPageSize(Number(value))
        setTimeout(() => {
          table.setPageSize(Number(value))
          // Reset to the top so resizing from near the bottom lands at the
          // start of the new result set, matching the page-nav buttons.
          scrollToTop()
        }, 150)
      }}
      size="small"
    />
  )
}

export const CursorPagination = ({
  goToFirstPage,
  goToPreviousPage,
  goToNextPage,
  canGoToFirstPage,
  canGoToPreviousPage,
  canGoToNextPage,
}: {
  goToFirstPage: () => void
  canGoToFirstPage: () => boolean
  goToPreviousPage: () => void
  goToNextPage: () => void
  canGoToPreviousPage: () => boolean
  canGoToNextPage: () => boolean
}) => {
  return (
    <PageNavigator
      mode="cursor"
      goToFirstPage={goToFirstPage}
      goToPreviousPage={goToPreviousPage}
      goToNextPage={goToNextPage}
      canGoFirstPage={canGoToFirstPage()}
      canGoPreviousPage={canGoToPreviousPage()}
      canGoNextPage={canGoToNextPage()}
    />
  )
}
